import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  SafetyOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { surveyService } from '../../services/survey.service';
import { GroupReport, QuestionReport } from '../../types/survey-questionnaire-report.types';
import { RichTextDisplay } from '../../components/common/RichTextDisplay';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

function MetaItem({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 12,
          color: '#8c8c8c',
          fontWeight: 500,
          marginBottom: 5,
        }}
      >
        {icon && <span style={{ lineHeight: 1 }}>{icon}</span>}
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#262626', lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function isHtml(str: string) {
  return /<[a-z][\s\S]*>/i.test(str);
}

const COVERAGE_LABELS: Record<string, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
  CITY: 'Municipal',
  CUSTOM: 'Personalizada',
};

const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC_AFTER_CLOSE: 'Público após encerramento',
  PUBLIC_AFTER_DATE: 'Público a partir de data',
  PARTICIPANTS_AFTER_CLOSE: 'Participantes após encerramento',
  PARTICIPANTS_AFTER_DATE: 'Participantes a partir de data',
  CONTRACTOR_ONLY: 'Apenas contratante',
};

const VALIDATION_LABELS: Record<string, string> = {
  LEVEL_1: 'Nível 1',
  LEVEL_2: 'Nível 2',
  LEVEL_3: 'Nível 3',
};

const QUESTION_TYPE_LABELS: Record<string, string> = {
  MULTIPLE_CHOICE: 'Múltipla escolha',
  CHECKBOX: 'Caixa de seleção',
  TEXT: 'Texto',
  NUMERIC: 'Numérico',
  RATING: 'Avaliação',
};

const GROUP_SCOPE_LABELS: Record<string, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
};

function BoolFlag({ value, label }: { value: boolean; label: string }) {
  return (
    <Space size={4}>
      {value ? (
        <CheckOutlined style={{ color: '#52c41a' }} />
      ) : (
        <CloseOutlined style={{ color: '#d9d9d9' }} />
      )}
      <Text type={value ? undefined : 'secondary'}>{label}</Text>
    </Space>
  );
}

function QuestionCard({ question, index }: { question: QuestionReport; index: number }) {
  const hasChoices = question.choices !== null && question.choices !== undefined;
  const hasSettings = question.settings !== null;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Tag>{index}</Tag>
          <Tag color="blue">{QUESTION_TYPE_LABELS[question.type] ?? question.type}</Tag>
          <Text strong>{question.title}</Text>
        </Space>
      }
      extra={
        question.required ? (
          <Tag color="red">Obrigatória</Tag>
        ) : (
          <Tag>Opcional</Tag>
        )
      }
    >
      {question.description && (
        <Paragraph type="secondary" style={{ marginBottom: hasSettings || hasChoices ? 12 : 0 }}>
          {question.description}
        </Paragraph>
      )}

      {hasSettings && question.settings && (
        <div style={{ marginBottom: hasChoices ? 12 : 0 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            Configurações
          </Text>
          <Space wrap size={[16, 8]}>
            <BoolFlag value={question.settings.randomizeChoices} label="Ordenar aleatoriamente" />
            <BoolFlag value={question.settings.includeNullOption} label="Incluir nulo" />
            <BoolFlag value={question.settings.includeDontKnowOption} label="Incluir não sei responder" />
            {question.type === 'CHECKBOX' && (
              <>
                {(question.settings.maxSelections ?? 0) > 0 && (
                  <Space size={4}>
                    <Text type="secondary">Máximo de opções:</Text>
                    <Text strong>{question.settings.maxSelections}</Text>
                    {question.settings.enforceSelectionLimit && (
                      <Tag color="orange" style={{ marginLeft: 4 }}>obrigatório</Tag>
                    )}
                  </Space>
                )}
              </>
            )}
          </Space>
        </div>
      )}

      {hasChoices && question.choices!.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            Opções de resposta ({question.choices!.length})
          </Text>
          <Space wrap>
            {question.choices!.map((choice) => (
              <Tag key={choice.value}>{choice.label}</Tag>
            ))}
          </Space>
        </div>
      )}
    </Card>
  );
}

function GroupSection({ group, groupIndex }: { group: GroupReport; groupIndex: number }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          background: '#fafafa',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 12,
        }}
      >
        <Row gutter={24} align="middle">
          <Col flex="auto">
            <Space>
              <Badge
                count={groupIndex}
                style={{ backgroundColor: '#1677ff' }}
              />
              <Text strong style={{ fontSize: 15 }}>
                {group.name}
              </Text>
            </Space>
          </Col>
          <Col>
            <Space size={8} wrap>
              <Tag>{GROUP_SCOPE_LABELS[group.scope] ?? group.scope}</Tag>
              {group.stateName && <Tag>{group.stateName}</Tag>}
              {group.showNameToParticipant ? (
                <Tag color="green">Nome visível ao participante</Tag>
              ) : (
                <Tag>Nome oculto ao participante</Tag>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {group.questions.length === 0 ? (
        <Text type="secondary" style={{ paddingLeft: 8 }}>
          Nenhuma pergunta neste grupo.
        </Text>
      ) : (
        group.questions.map((q) => (
          <QuestionCard key={q.id} question={q} index={q.order} />
        ))
      )}
    </div>
  );
}

const SurveyQuestionnaireReport: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['survey-questionnaire-report', surveyId],
    queryFn: () => surveyService.getQuestionnaireReport(surveyId!),
    enabled: !!surveyId,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Relatório não disponível"
          description="Não foi possível carregar o relatório do questionário."
          action={
            <Button onClick={() => navigate(-1)}>Voltar</Button>
          }
        />
      </div>
    );
  }

  const { basicData, questions } = report;
  const hasGroups = questions.groups.length > 0;
  const hasUngrouped = questions.ungrouped.length > 0;

  const formatDateTime = (iso: string) => dayjs(iso).format('DD/MM/YYYY HH:mm');

  const coverageLabel = COVERAGE_LABELS[basicData.coverage ?? ''] ?? basicData.coverage ?? '-';

  const locationParts = [basicData.location.stateName, basicData.location.cityName].filter(Boolean);
  const locationLabel = locationParts.length > 0 ? locationParts.join(' — ') : '-';

  const visibilityLabel = VISIBILITY_LABELS[basicData.visibilityLevel] ?? basicData.visibilityLevel;

  const needsDisclosureDate =
    basicData.visibilityLevel === 'PUBLIC_AFTER_DATE' ||
    basicData.visibilityLevel === 'PARTICIPANTS_AFTER_DATE';

  const totalQuestions =
    questions.groups.reduce((acc, g) => acc + g.questions.length, 0) + questions.ungrouped.length;

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Button
          icon={<FileTextOutlined />}
          onClick={() => window.print()}
        >
          Imprimir / Salvar PDF
        </Button>
      </Space>

      <Title level={3} style={{ marginBottom: 4 }}>
        Relatório do Questionário
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {basicData.name}
      </Text>

      {/* Group 1 - Dados Básicos */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            Dados Básicos
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {/* Name */}
        <Title level={4} style={{ marginBottom: basicData.description ? 8 : 0 }}>
          {basicData.name}
        </Title>

        {/* Description */}
        {basicData.description && (
          <div style={{ marginBottom: 0, color: '#595959', maxWidth: 700 }}>
            {isHtml(basicData.description) ? (
              <RichTextDisplay html={basicData.description} />
            ) : (
              <Paragraph style={{ marginBottom: 0, color: 'inherit' }}>
                {basicData.description}
              </Paragraph>
            )}
          </div>
        )}

        <Divider style={{ margin: '20px 0' }} />

        {/* Metadata grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '20px 32px',
          }}
        >
          <MetaItem icon={<CalendarOutlined />} label="Período de coleta">
            <Space size={6}>
              <span>{formatDateTime(basicData.collectionPeriod.startDate)}</span>
              <ArrowRightOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />
              <span>{formatDateTime(basicData.collectionPeriod.endDate)}</span>
            </Space>
          </MetaItem>

          <MetaItem icon={<EnvironmentOutlined />} label="Abrangência">
            <Tag style={{ margin: 0 }}>{coverageLabel}</Tag>
          </MetaItem>

          {locationLabel !== '-' && (
            <MetaItem icon={<EnvironmentOutlined />} label="Localização geográfica">
              {locationLabel}
            </MetaItem>
          )}

          <MetaItem icon={<EyeOutlined />} label="Nível de visibilidade">
            <Tag color="blue" style={{ margin: 0 }}>{visibilityLabel}</Tag>
          </MetaItem>

          {needsDisclosureDate && (
            <MetaItem icon={<CalendarOutlined />} label="Divulgação do resultado">
              {basicData.disclosureDate ? (
                formatDateTime(basicData.disclosureDate)
              ) : (
                <Text type="secondary">Não definida</Text>
              )}
            </MetaItem>
          )}

          <MetaItem icon={<SafetyOutlined />} label="Nível de validação">
            {basicData.validationLevel ? (
              <Space size={6} wrap>
                <Tag color="purple" style={{ margin: 0 }}>
                  {VALIDATION_LABELS[basicData.validationLevel.minimum] ?? basicData.validationLevel.minimum}
                </Tag>
                {basicData.validationLevel.platformFloorApplied && (
                  <Tag color="orange" style={{ margin: 0 }}>Piso da plataforma</Tag>
                )}
                {basicData.validationLevel.effective !== basicData.validationLevel.minimum && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    efetivo: {VALIDATION_LABELS[basicData.validationLevel.effective] ?? basicData.validationLevel.effective}
                  </Text>
                )}
              </Space>
            ) : (
              <Text type="secondary">Não configurado</Text>
            )}
          </MetaItem>
        </div>
      </Card>

      {/* Group 2 - Perguntas */}
      <Card
        title={
          <Space>
            <QuestionCircleOutlined />
            Perguntas
            <Tag>{totalQuestions} pergunta{totalQuestions !== 1 ? 's' : ''}</Tag>
          </Space>
        }
      >
        {totalQuestions === 0 ? (
          <Text type="secondary">Nenhuma pergunta cadastrada nesta pesquisa.</Text>
        ) : (
          <>
            {hasGroups && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <UnorderedListOutlined />
                    <Text strong>Grupos de Perguntas</Text>
                    <Tag>{questions.groups.length} grupo{questions.groups.length !== 1 ? 's' : ''}</Tag>
                  </Space>
                </div>

                {questions.groups.map((group, idx) => (
                  <GroupSection key={group.id} group={group} groupIndex={idx + 1} />
                ))}
              </>
            )}

            {hasGroups && hasUngrouped && (
              <Divider orientation="left" plain>
                <Text type="secondary">Perguntas sem grupo</Text>
              </Divider>
            )}

            {hasUngrouped && (
              <div>
                {!hasGroups && (
                  <div style={{ marginBottom: 12 }}>
                    <Space>
                      <UnorderedListOutlined />
                      <Text strong>Perguntas</Text>
                    </Space>
                  </div>
                )}
                {questions.ungrouped.map((q) => (
                  <QuestionCard key={q.id} question={q} index={q.order} />
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default SurveyQuestionnaireReport;
